import { textNodeToHTML } from '@contentstack/utils/dist/lib/helper/enumerate-entries';
import { findEmbeddedItems } from '@contentstack/utils/dist/lib/helper/find-embeded-object';
import { defaultNodeOption } from '@contentstack/utils/dist/lib/options/default-node-options';
import React from 'react';

/**
 * @typedef EnumerateProps
 * @property {EntryEmbedable} entry
 * @property {string[]} paths
 * @property {RenderOption} renderOption
 * @property {ToGoBox} toGoBox

 * @param {EnumerateProps} option
 */
export function enumerateKeys(option) {
	for (const key of option.paths) {
		findRenderContent(key, option.entry, option.toGoBox, (content) => {
			return enumerateContents(content, option.renderOption, (metadata) => {
				return findEmbeddedItems(metadata, option.entry)[0];
			});
		});
	}
}

/**
 * @typedef {(content: any) => import('react').ReactNode[][]} SingleRenderFunc

 * @param {string} keyPath The dot notation key to check.
 * @param {Object} entry The object to enumerate
 * @param {ToGoBox} toGoBox The array to populate with output.
 * @param {SingleRenderFunc} render The render options.
 */
function findRenderContent(keyPath, entry, toGoBox, render) {
	getContent(keyPath.split('.'), entry, toGoBox, render);
}

/**
 *
 * @param {string[]} keys The dot notation keys to check.
 * @param {Object} object The object to enumerate
 * @param {ToGoBox} toGoBox The array to populate with output.
 * @param {SingleRenderFunc} render The render options.
 */
function getContent(keys, object, toGoBox, render) {
	if (!keys) {
		return;
	}

	const key = keys[0];
	if (keys.length === 1 && object[keys[0]]) {
		toGoBox.push(render(object[key]));
	} else if (keys.length > 0 && !!object[key]) {
		const toEnumerate = [];

		if (Array.isArray(object[key])) {
			toEnumerate.push(...object[key]);
		} else if (typeof object[key] === 'object') {
			toEnumerate.push(object[key]);
		}

		toEnumerate.forEach((obj, j) => {
			getContent(keys.slice(1), obj[j], toGoBox, render);
		});
	}
}

/** @see enumerateContents `@contentstack/utils/dist/lib/helper/enumerate-entries` */
function enumerateContents(content, renderOption, renderEmbed) {
	if (!Array.isArray(content) && content.type !== 'doc') {
		return content;
	} else if (Array.isArray(content)) {
		return [...content.map((doc) => enumerateContents(doc, renderOption, renderEmbed))];
	}

	const commonRenderOption = Object.assign({}, defaultNodeOption, renderOption);
	return nodeChildrenToHTML(content.children, commonRenderOption, renderEmbed);
}

/**
 * Nearly identical, except no converting to string ('join')
 * @see enumerateContents `@contentstack/utils/dist/lib/helper/enumerate-entries`
 */
function nodeChildrenToHTML(nodes, renderOption, renderEmbed) {
	return nodes.map((node) => nodeToHTML(node, renderOption, renderEmbed));
}

/** @see enumerateContents `@contentstack/utils/dist/lib/helper/enumerate-entries` */
function nodeToHTML(node, renderOption, renderEmbed) {
	if (!node.type) {
		const textNode = textNodeToHTML(node, renderOption);
		let element = cloneOrFragment(textNode, renderOption.localIndex++);

		// Text-nodes can have a 'break' property - add a <br/> tag.
		if ('break' in node && node.break) {
			let newChildren = Array.isArray(element.props.children) ? [...element.props.children] : [element.props.children];
			newChildren = newChildren.map((nc) =>
				!!nc && typeof nc === 'string' ? nc.replace('\n', '').replace(/<br\s?\/>/i, '') : nc
			);
			element = React.cloneElement(element, {}, [...newChildren, <br key={renderOption.localIndex++} />]);
		}

		return element;
	} else {
		const next = (nodes) => nodeChildrenToHTML(nodes, renderOption, renderEmbed);
		// Check for type existence, and presence in our options.
		const optionKey = !node.type || !(node.type in renderOption) ? 'default' : node.type;
		const rendered = renderOption[optionKey](node, next);
		return cloneOrFragment(rendered, renderOption.localIndex++);
	}
}

/**
 * Make a centralized function to detect a valid element or wrap in fragment.
 * 	Attaches the given key to the new element to be sure it's properly indexed within an array.
 */
function cloneOrFragment(node, key) {
	return React.isValidElement(node) ? (
		React.cloneElement(node, { key })
	) : (
		<React.Fragment key={key}>{node}</React.Fragment>
	);
}